import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import {
  Loader2,
  UserPlus,
  Pause,
  Play,
  UserMinus,
  ShieldCheck,
} from "lucide-react";
import api from "../lib/api";
import Layout from "../components/Layout";
import { useAuth } from "../context/AuthContext";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

const STATUS_STYLES = {
  active: "bg-[#4D5B46]/30 text-[#A3C293] border-[#4D5B46]/50",
  invited: "bg-champagne/10 text-champagne border-champagne/30",
  temporary: "bg-champagne/15 text-champagne border-champagne/40",
  suspended: "bg-[#5C2B29]/30 text-[#E5A09E] border-[#5C2B29]/50",
  expired: "bg-elevated text-textmuted border-seam",
  removed: "bg-elevated text-textmuted border-seam opacity-70",
};

const InviteDialog = ({ open, onOpenChange, onSaved, meta, adminSelf }) => {
  const [form, setForm] = useState({
    email: "",
    name: "",
    role: "executive_assistant",
    reason: "",
    expires_at: "",
    permissions: null,
  });
  const [saving, setSaving] = useState(false);
  const [customize, setCustomize] = useState(false);

  useEffect(() => {
    if (open) {
      setForm({
        email: "",
        name: "",
        role: "executive_assistant",
        reason: "",
        expires_at: "",
        permissions: null,
      });
      setCustomize(false);
    }
  }, [open]);

  const roleOptions = Object.entries(meta?.admin_roles || {});
  const isCEO = adminSelf?.role === "owner_ceo";
  const roleDefaults = meta?.default_permissions?.[form.role] || [];
  const chosen = form.permissions ?? roleDefaults;

  const togglePerm = (p) => {
    const next = chosen.includes(p) ? chosen.filter((x) => x !== p) : [...chosen, p];
    setForm((f) => ({ ...f, permissions: next }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email.trim()) {
      toast.error("Email is required");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form };
      if (!payload.name) delete payload.name;
      if (!payload.reason) delete payload.reason;
      if (!payload.expires_at) delete payload.expires_at;
      if (!customize) delete payload.permissions;
      await api.post("/enterprise/admins", payload);
      toast.success("Admin invited");
      onSaved?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Invite failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="invite-admin-dialog"
        className="bg-surface border-seam max-w-lg text-textmain max-h-[90vh] overflow-y-auto"
      >
        <DialogHeader>
          <DialogTitle className="font-heading text-3xl">Invite administrator</DialogTitle>
          <DialogDescription className="text-textmuted">
            Every admin uses their own authenticated account. Least-privilege by default.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4 mt-2">
          <div>
            <label className="block label-eyebrow mb-2">Email</label>
            <input
              required
              type="email"
              value={form.email}
              onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
              data-testid="invite-email-input"
              className="w-full bg-obsidian border border-seam rounded-md px-4 py-2.5 focus:outline-none focus:border-champagne focus:ring-1 focus:ring-champagne/40"
              placeholder="admin@rsvp.example"
            />
          </div>

          <div>
            <label className="block label-eyebrow mb-2">Name (optional)</label>
            <input
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              className="w-full bg-obsidian border border-seam rounded-md px-4 py-2.5 focus:outline-none focus:border-champagne focus:ring-1 focus:ring-champagne/40"
            />
          </div>

          <div>
            <label className="block label-eyebrow mb-2">Role</label>
            <Select
              value={form.role}
              onValueChange={(v) => setForm((f) => ({ ...f, role: v, permissions: null }))}
            >
              <SelectTrigger data-testid="invite-role-select" className="bg-obsidian border-seam text-textmain">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-surface border-seam text-textmain">
                {roleOptions.map(([k, label]) => (
                  <SelectItem key={k} value={k} disabled={k === "owner_ceo" && !isCEO}>
                    {label}
                    {k === "owner_ceo" && !isCEO ? " (CEO only)" : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block label-eyebrow mb-2">Expires (optional)</label>
              <input
                type="date"
                value={form.expires_at}
                onChange={(e) => setForm((f) => ({ ...f, expires_at: e.target.value }))}
                className="w-full bg-obsidian border border-seam rounded-md px-3 py-2.5 focus:outline-none focus:border-champagne text-textmain [color-scheme:dark]"
              />
            </div>
            <div>
              <label className="block label-eyebrow mb-2">Reason</label>
              <input
                value={form.reason}
                onChange={(e) => setForm((f) => ({ ...f, reason: e.target.value }))}
                className="w-full bg-obsidian border border-seam rounded-md px-3 py-2.5 focus:outline-none focus:border-champagne"
                placeholder="Consultant, Auditor…"
              />
            </div>
          </div>

          <div className="border border-seam rounded-md p-4 bg-obsidian/50">
            <label className="flex items-center justify-between cursor-pointer">
              <div>
                <p className="text-sm text-textmain">Customize permissions</p>
                <p className="text-xs text-textmuted mt-0.5">
                  {customize ? `${chosen.length} selected` : `${roleDefaults.length} default for this role`}
                </p>
              </div>
              <input
                type="checkbox"
                checked={customize}
                onChange={(e) => {
                  setCustomize(e.target.checked);
                  if (e.target.checked) setForm((f) => ({ ...f, permissions: roleDefaults.slice() }));
                  else setForm((f) => ({ ...f, permissions: null }));
                }}
                className="w-4 h-4 accent-champagne"
              />
            </label>
            {customize && (
              <div className="mt-3 max-h-48 overflow-y-auto grid grid-cols-2 gap-1 text-xs">
                {(meta?.permissions || []).map((p) => (
                  <label key={p} className="flex items-center gap-2 text-textmuted cursor-pointer py-0.5">
                    <input
                      type="checkbox"
                      checked={chosen.includes(p)}
                      onChange={() => togglePerm(p)}
                      className="w-3 h-3 accent-champagne"
                    />
                    <span className="font-mono">{p}</span>
                  </label>
                ))}
              </div>
            )}
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              className="px-5 py-2.5 rounded-md border border-seam text-textmuted hover:text-textmain hover:border-champagne/40 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              data-testid="invite-submit-btn"
              className="px-6 py-2.5 rounded-md bg-champagne text-obsidian font-medium hover:bg-champagne-soft transition-all shadow-[0_0_20px_rgba(212,175,55,0.15)] disabled:opacity-60 inline-flex items-center gap-2"
            >
              {saving && <Loader2 className="w-4 h-4 animate-spin" />}
              Send invitation
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const AdminAccessCenter = () => {
  const { admin: adminSelf, hasPermission } = useAuth();
  const canManage = hasPermission("admins.manage");
  const [admins, setAdmins] = useState([]);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [inviteOpen, setInviteOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const [a, m] = await Promise.all([
        api.get("/enterprise/admins"),
        api.get("/enterprise/meta"),
      ]);
      setAdmins(a.data);
      setMeta(m.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not load admins");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const action = async (adminId, kind) => {
    try {
      await api.post(`/enterprise/admins/${adminId}/${kind}`);
      toast.success("Updated");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Action failed");
    }
  };

  return (
    <Layout>
      <section className="mb-6 flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
        <div className="max-w-2xl">
          <p className="label-eyebrow">Governance</p>
          <h1 className="font-heading text-4xl sm:text-5xl tracking-tight mt-2">
            Admin Access Center
          </h1>
          <p className="text-textmuted mt-3 leading-relaxed">
            Every administrator, their role, scopes, permissions and activity — one authoritative
            list. Removing access preserves historical audit.
          </p>
        </div>
        {canManage && (
          <button
            type="button"
            onClick={() => setInviteOpen(true)}
            data-testid="invite-admin-btn"
            className="inline-flex items-center gap-2 self-start bg-champagne text-obsidian hover:bg-champagne-soft font-medium rounded-md px-5 py-2.5 transition-all shadow-[0_0_20px_rgba(212,175,55,0.15)]"
          >
            <UserPlus className="w-4 h-4" strokeWidth={1.7} />
            Invite admin
          </button>
        )}
      </section>

      {loading ? (
        <div className="flex justify-center py-20">
          <Loader2 className="w-6 h-6 text-champagne animate-spin" />
        </div>
      ) : (
        <div className="regal-card overflow-hidden">
          <div className="px-4 py-3 border-b border-seam bg-elevated/40 flex items-center justify-between">
            <span className="label-eyebrow">Administrators</span>
            <span className="text-xs text-textmuted">{admins.length}</span>
          </div>
          <div className="divide-y divide-seam">
            {admins.map((a) => {
              const roleLabel = meta?.admin_roles?.[a.role] || a.role;
              return (
                <div
                  key={a.id}
                  data-testid={`admin-row-${a.id}`}
                  className="px-5 py-4 hover:bg-elevated/40 transition-colors"
                >
                  <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <ShieldCheck className="w-4 h-4 text-champagne" strokeWidth={1.5} />
                        <p className="text-sm text-textmain font-medium">{a.name || a.email}</p>
                        <span
                          className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border ${
                            STATUS_STYLES[a.status] || STATUS_STYLES.active
                          }`}
                        >
                          {a.status}
                        </span>
                      </div>
                      <p className="text-xs text-textmuted mt-1">
                        {a.email} · <span className="text-champagne">{roleLabel}</span>
                        {a.expires_at && ` · expires ${a.expires_at.slice(0, 10)}`}
                      </p>
                      <p className="text-[11px] text-textmuted mt-1">
                        {(a.permissions || []).length} permissions ·{" "}
                        {(a.scopes || []).length} scope{a.scopes?.length === 1 ? "" : "s"}
                        {a.last_login_at ? ` · last login ${a.last_login_at.slice(0, 10)}` : " · never signed in"}
                      </p>
                    </div>

                    {canManage && a.status !== "removed" && (
                      <div className="flex items-center gap-2 flex-wrap">
                        {a.status === "suspended" ? (
                          <button
                            type="button"
                            onClick={() => action(a.id, "reactivate")}
                            data-testid={`admin-reactivate-${a.id}`}
                            className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md border border-champagne/40 text-champagne hover:bg-champagne/10 transition-colors"
                          >
                            <Play className="w-3.5 h-3.5" strokeWidth={1.6} />
                            Reactivate
                          </button>
                        ) : (
                          <button
                            type="button"
                            onClick={() => action(a.id, "suspend")}
                            data-testid={`admin-suspend-${a.id}`}
                            className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md border border-seam text-textmuted hover:text-textmain transition-colors"
                          >
                            <Pause className="w-3.5 h-3.5" strokeWidth={1.6} />
                            Suspend
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => {
                            if (window.confirm(`Remove access for ${a.email}? History will remain.`)) {
                              action(a.id, "remove");
                            }
                          }}
                          data-testid={`admin-remove-${a.id}`}
                          className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md border border-seam text-textmuted hover:text-[#E5A09E] hover:border-[#5C2B29]/50 transition-colors"
                        >
                          <UserMinus className="w-3.5 h-3.5" strokeWidth={1.6} />
                          Remove
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <InviteDialog
        open={inviteOpen}
        onOpenChange={setInviteOpen}
        onSaved={() => load()}
        meta={meta}
        adminSelf={adminSelf}
      />
    </Layout>
  );
};

export default AdminAccessCenter;
